import { useState, useEffect, useCallback } from "react";
import { commentsAPI } from "./commentsAPI";
import { useAuthStore } from "../../store/authStore";
import Comment from "../../components/Comment";

export default function CommentsList({ reviewId }) {
  const { user } = useAuthStore();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Memoize fetchComments để tránh re-render liên tục
  const fetchComments = useCallback(async () => {
    if (!reviewId) return;

    setLoading(true);
    setError("");

    try {
      const result = await commentsAPI.getCommentsByReview(reviewId);

      if (result.success) {
        // Lấy tất cả comments từ backend
        const commentsData = result.data.comments || [];

        // Chỉ hiển thị top-level comments (không có parentComment)
        const topLevelComments = commentsData.filter(
          (comment) => !comment.parentComment && !comment.isDeleted
        );

        setComments(topLevelComments);
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error("Error fetching comments:", err);
      setError("Đã xảy ra lỗi khi tải comments");
    } finally {
      setLoading(false);
    }
  }, [reviewId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();

    if (!newComment.trim()) return;

    setSubmitting(true);

    try {
      const result = await commentsAPI.createComment({
        content: newComment,
        review: reviewId,
      });

      if (result.success) {
        setNewComment("");
        await fetchComments(); // Refresh comments
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Đã xảy ra lỗi khi tạo comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = async (commentId, content) => {
    try {
      const result = await commentsAPI.updateComment(commentId, content);

      if (result.success) {
        await fetchComments(); // Refresh comments
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Đã xảy ra lỗi khi cập nhật comment");
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const result = await commentsAPI.deleteComment(commentId);

      if (result.success) {
        await fetchComments(); // Refresh comments
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Đã xảy ra lỗi khi xóa comment");
    }
  };

  const handleReplyComment = async (parentCommentId, content) => {
    try {
      const result = await commentsAPI.createComment({
        content,
        review: reviewId,
        parentComment: parentCommentId,
      });

      if (result.success) {
        // Sau khi tạo reply, cập nhật comment count
        await fetchComments();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Đã xảy ra lỗi khi tạo reply");
    }
  };

  if (!reviewId) {
    return null;
  }

  return (
    <div className="comments">
      <h3 className="comments__title">Bình luận ({comments.length})</h3>

      {/* New Comment Form */}
      {user ? (
        <form onSubmit={handleSubmitComment} className="comments__new-form">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            className="comments__textarea"
            rows={4}
            placeholder="Viết bình luận của bạn..."
          />
          <div className="comments__form-actions">
            <button
              type="submit"
              disabled={submitting || !newComment.trim()}
              className="btn btn--primary"
            >
              {submitting ? "Đang gửi..." : "Gửi bình luận"}
            </button>
          </div>
        </form>
      ) : (
        <div className="comments__login-prompt">
          <p>
            Bạn cần{" "}
            <a href="/login" className="comments__login-link">
              đăng nhập
            </a>{" "}
            để bình luận
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="comments__error">
          <p>{error}</p>
          <button
            onClick={() => setError("")}
            className="btn btn--ghost btn--sm"
          >
            Đóng
          </button>
        </div>
      )}

      {/* Comments List */}
      {loading ? (
        <div className="comments__loading">
          <div className="loading-spinner"></div>
          <p>Đang tải bình luận...</p>
        </div>
      ) : comments.length === 0 ? (
        <div className="comments__empty">
          <p>Chưa có bình luận nào. Hãy là người đầu tiên bình luận!</p>
        </div>
      ) : (
        <div className="comments__list">
          {comments.map((comment) => (
            <Comment
              key={comment._id || comment.id}
              comment={comment}
              reviewId={reviewId}
              onEdit={handleEditComment}
              onDelete={handleDeleteComment}
              onReply={handleReplyComment}
              onRefresh={fetchComments} // Thêm callback để refresh
            />
          ))}
        </div>
      )}
    </div>
  );
}
